import { values } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAtom } from 'jotai';
import { PROJECT_STATUS, USER_ROLE } from '../lib/constants';
import { cln } from '../lib/utils';
import { Project } from '../types';
import { Select } from './Select';
import { authAtom, changeProjectStatusConfirmDialogAtom } from '../atoms';
import { PATHS } from '../lib/paths';
import { Summary } from './Summary';

interface Props {
  project: Project;
}

/* eslint-disable react/destructuring-assignment */
export function ProjectDetails({ project }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [auth] = useAtom(authAtom);
  const [, setStatusChangeDialogAtom] = useAtom(changeProjectStatusConfirmDialogAtom);

  const handleShareClick = () => {
    navigator.clipboard.writeText(`${import.meta.env.VITE_DOMAIN}${PATHS.projectPath(project.id)}`);
    toast.success(t('common.copiedLink'));
  };

  const handleStatusChange = (e: any) => {
    const status = e.target.value;
    if (status !== project.status)
      setTimeout(() => setStatusChangeDialogAtom({ isOpen: true, project, status }), 1);
  };

  const isOptionDisabled = (option: string) => {
    const options = values(PROJECT_STATUS) as string[];
    const currentIndex = options.indexOf(project.status);

    if (currentIndex === options.length - 1) return true;
    if (option === options[currentIndex + 1]) return false;
    return true;
  };

  const summaryComponent = () => (
    <span
      className={cln(
        'badge font-medium text-label',
        project.status === PROJECT_STATUS.CREATED && 'bg-status-created',
        project.status === PROJECT_STATUS.DEVELOP && 'bg-status-develop',
        project.status === PROJECT_STATUS.DONE && 'bg-status-done',
        project.status === PROJECT_STATUS.ARCHIVED && 'bg-status-archived',
      )}
    >
      {t(`project.status.${project.status}`)}
    </span>
  );
  return (
    <Summary title={project.name} summaryComponent={summaryComponent()} subtitle={project.key}>
      <div className="grid grid-cols-[min-content_auto] gap-5 pt-10 pb-3">
        <span className="font-semibold">{t('project.createdAt')}</span>
        <span>{new Date(project.createdAt).toLocaleDateString()}</span>
        <span className="font-semibold">{t('project.startDate')}</span>
        <span>{project.startDate ? new Date(project.startDate).toLocaleDateString() : '-'}</span>
        <span className="font-semibold">{t('project.doneDate')}</span>
        <span>{project.doneDate ? new Date(project.doneDate).toLocaleDateString() : '-'}</span>
        <span className="font-semibold">{t('project.key')}</span>
        <span>{project.key}</span>
        <div className="flex justify-end items-start gap-4 col-span-2">
          {auth?.user?.role === USER_ROLE.SUPERADMIN && (
            <Select
              defaultValue={project.status}
              onChange={handleStatusChange}
              disabled={project.status === PROJECT_STATUS.ARCHIVED}
            >
              {values(PROJECT_STATUS).map((s) => (
                <option value={s} key={s} disabled={isOptionDisabled(s)}>
                  {t(`project.status.${s}`)}
                </option>
              ))}
            </Select>
          )}
          <button
            className="primary-outline"
            onClick={() => navigate(PATHS.projectPath(project.id))}
          >
            <i className="ri-file-info-line" />
            {t('common.details')}
          </button>
          <button className="primary-outline whitespace-nowrap" onClick={handleShareClick}>
            <i className="ri-share-forward-line" />
            {t('common.copyLink')}
          </button>
        </div>
      </div>
    </Summary>
  );
}
